import { yupResolver } from '@hookform/resolvers/yup'
import { useRef, useState } from 'react'
import * as yup from 'yup'

import { Controller, FormProvider, useForm } from 'react-hook-form'

import { ErrorMessage } from '@/app/components/ErrorMessage'
import { Input } from '@/app/components/Input'
import {
  INotificationStatus,
  useNotificationContext,
} from '@/app/components/Notification'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { SecondaryButton } from '@/app/components/SecondaryButton'
import { Headline2 } from '@/app/components/Typography'
import useInvitationMutation from '@/app/hooks/data-hooks/useInvitationMutation'
import { useMyUser } from '@/app/hooks/data-hooks/useMyUser'
import { useUserMutation } from '@/app/hooks/data-hooks/useUserMutation'
import { useRouteObserver } from '@/app/hooks/useRouterObserver'
import { GroupAdd } from '@/app/icons/GroupAdd'
import { LinkedInIconInverted } from '@/app/icons/LinkedInIconInverted'
import SuitcaseIcon from '@/app/icons/SuitcaseIcon/SuitcaseIcon'
import { getErrorMessage } from '@/app/lib/getErrorMessage'
import { ProgressBar } from '@/app/modules/shared/components/progress-bar'
import { InviteType } from '@/types/invitation'
import { UserInclude, UserRole } from '@/types/user'
import {
  InvitationFormValues,
  InvitationModal,
  InvitedUser,
} from './InvitationModal'
import { useNamespacedIntl } from '../lib/useNamespacedIntl'

export type UserBasicInfoStepValues = {
  linkedIn?: string
  jobRole: string
  invitedUsers: InvitedUser[]
}

type UserBasicInfoStepProps = {
  onContinue: () => Promise<void>
  maxSteps: number
  currentStep: number
}

export function UserBasicInfoStep({
  onContinue,
  maxSteps,
  currentStep,
}: UserBasicInfoStepProps) {
  const t = useNamespacedIntl('UserOnboarding')
  const { data: { data: user } = {}, isLoading: isLoadingUser } = useMyUser({
    include: [UserInclude.Company],
  })
  const { isLoading: isRedirecting } = useRouteObserver()
  const { setMessage } = useNotificationContext()

  const { mutateAsync: updateUser } = useUserMutation(user?._id ?? '')
  const { mutateAsync: createInvitation } = useInvitationMutation()

  const userCanInviteCollaborators = user?.roles.includes(UserRole.Employer)
  const isLoading = isLoadingUser || isRedirecting

  const formSchema = yup.object<UserBasicInfoStepValues>().shape({
    linkedIn: yup
      .string()
      .url()
      .test('linkedin', t('validation_linkedin_required'), (value) => {
        if (!value) {
          return true
        }

        return !!value?.toLocaleLowerCase().includes('linkedin.com')
      })
      .optional(),
    jobRole: yup.string().default(''),
    invitedUsers: yup
      .array()
      .of(
        yup.object().shape({
          email: yup.string().email().required(),
          name: yup.string(),
          role: yup.string().oneOf(Object.values(UserRole)).required(),
        })
      )
      .required()
      .default([]),
  })

  const methods = useForm<UserBasicInfoStepValues>({
    resolver: yupResolver(formSchema),
    disabled: isLoading,
    values: {
      jobRole: user?.jobRole ?? '',
      linkedIn: user?.linkedIn ?? '',
      invitedUsers: [],
    },
  })

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = methods

  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const handleInvitationSubmit = async ({
    invitedUsers = [],
  }: InvitationFormValues) => {
    setValue('invitedUsers', invitedUsers)
    setIsInvitationModalOpen(false)
  }

  const handleOnSubmit = async (data: UserBasicInfoStepValues) => {
    if (!user?._id) {
      return
    }

    try {
      await updateUser({
        firstName: user.firstName,
        lastName: user.lastName,
        jobRole: data.jobRole,
        linkedIn: data.linkedIn,
      })
      await Promise.all(
        (data.invitedUsers || []).map(({ email, role }) =>
          createInvitation({
            email,
            role,
            inviteType: InviteType.User,
          })
        )
      )
      await onContinue()
    } catch {
      setMessage(t('account_update_failed'), INotificationStatus.ERROR)
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        className="flex h-full px-6 pb-3 md:px-0 md:py-12"
        onSubmit={handleSubmit(handleOnSubmit)}
        ref={formRef}
      >
        <div className="flex w-full flex-col gap-6">
          <Headline2 className="!font-sans font-semibold">
            {t('hang_on_tell_more')}
          </Headline2>
          {errors.root?.serverError?.message && (
            <ErrorMessage
              message={errors.root?.serverError?.message}
              className="mt-2 text-left"
            />
          )}
          <div className="flex flex-col gap-1 text-mydra-black md:gap-4">
            <label htmlFor="jobRole" className="text-sm font-medium md:text-base">
              {t('which_is_your_job_role_you_can_skip')}
            </label>
            <Controller
              control={control}
              name="jobRole"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id="jobRole"
                  startDecoration={
                    <SuitcaseIcon className="h-4 w-4 text-mydra-black" />
                  }
                  placeholder={t('which_is_your_job_role')}
                  errorMessage={getErrorMessage('jobRole', errors)}
                  className="w-full text-sm"
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-1 text-mydra-black md:gap-4">
            <label htmlFor="linkedIn" className="text-sm font-medium md:text-base">
              {t('whats_your_linkedin')}
            </label>
            <Controller
              control={control}
              name="linkedIn"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id="linkedIn"
                  startDecoration={
                    <LinkedInIconInverted className="h-4 w-4 text-mydra-black" />
                  }
                  placeholder="https://www.linkedin.com/in/username"
                  errorMessage={getErrorMessage('linkedIn', errors)}
                  className="w-full text-sm"
                />
              )}
            />
          </div>
          <div className="flex flex-col items-end gap-1">
            <SecondaryButton
              small
              className="flex items-center"
              disabled={!userCanInviteCollaborators}
              title={
                userCanInviteCollaborators
                  ? t('click_to_invite_collaborators')
                  : t('no_permission_to_invite_collaborators')
              }
              onClick={(e) => {
                e.preventDefault()
                setIsInvitationModalOpen(true)
              }}
            >
              <GroupAdd className="mr-2 h-5 w-5" />
              {t('invite_collaborators')}
            </SecondaryButton>
            {watch('invitedUsers').length > 0 && (
              <div className="flex gap-2">
                <span className="text-sm text-mydra-black">
                  {t('x_collaborators_invited', {
                    count: watch('invitedUsers').length,
                  })}
                </span>
                <button
                  type="button"
                  className="text-sm font-semibold text-mydra-black underline"
                  onClick={() => setValue('invitedUsers', [])}
                >
                  {t('clear')}
                </button>
              </div>
            )}
          </div>
          <div className="mt-auto flex w-full md:justify-end">
            <ProgressBar
              max={maxSteps}
              progress={currentStep}
              className="mr-auto self-center md:w-32"
            />
            <PrimaryButton
              type="submit"
              disabled={isLoading}
              isLoading={isSubmitting}
              className="w-full md:w-auto"
            >
              {t('continue')}
            </PrimaryButton>
          </div>
        </div>
      </form>
      <InvitationModal
        isOpen={isInvitationModalOpen}
        onClose={() => setIsInvitationModalOpen(false)}
        invitedUsers={watch('invitedUsers')}
        onSubmit={handleInvitationSubmit}
      />
    </FormProvider>
  )
}
